import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sheet, SheetHeader, SheetTitle, SheetBody, SheetActions } from './ui/Sheet';
import { Button } from './ui/Button';
import { API_URL } from '../utils/api';
import './OrderTypeSheet.css';

interface RestaurantConfig {
  id: string;
  dineInSupported: boolean;
  acceptsPreorders: boolean;
  tableCount: number;
  todayHours?: { open: string; close: string } | null;
}

interface CartItem {
  productId: string;
  quantity: number;
  price: number;
  name?: string;
  notes?: string;
  customization?: Record<string, unknown>;
}

interface OrderTypeSheetProps {
  open: boolean;
  onClose: () => void;
  restaurant: RestaurantConfig;
  items: CartItem[];
  customerId?: string | null;
  fcmToken?: string | null;
  onSignInRequired?: () => void;
}

const PHONE_REGEX = /^\+?[1-9]\d{7,14}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

export function OrderTypeSheet({
  open,
  onClose,
  restaurant,
  items,
  customerId,
  fcmToken,
  onSignInRequired,
}: OrderTypeSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Receive: pickup vs at-table (with table number).
  const [atTable, setAtTable] = useState(false);
  const [tableNumber, setTableNumber] = useState<string>('');

  // Timing: now vs scheduled (free time input).
  const [scheduled, setScheduled] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [showGuestPhone, setShowGuestPhone] = useState(false);

  const canDineIn = restaurant.dineInSupported && restaurant.tableCount > 0;
  const canPreorder = restaurant.acceptsPreorders;
  const isAuthenticated = !!customerId;
  const phoneValid = PHONE_REGEX.test(phone.trim());

  const total = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const totalLabel = total.toLocaleString('ru');

  // Validate table number
  const tableNumberInt = parseInt(tableNumber, 10);
  const tableValid = !atTable || (
    Number.isInteger(tableNumberInt) &&
    tableNumberInt >= 1 &&
    tableNumberInt <= restaurant.tableCount
  );

  // Validate scheduled time against today's open hours
  const todayHours = restaurant.todayHours ?? null;
  const timeValid = !scheduled || (() => {
    if (!TIME_RE.test(scheduledTime)) return false;
    if (!todayHours) return true;
    const t = timeToMinutes(scheduledTime);
    return t >= timeToMinutes(todayHours.open) && t <= timeToMinutes(todayHours.close);
  })();

  const submit = async () => {
    if (atTable && !tableValid) {
      setError(t('orderType.tableInvalid', {
        defaultValue: `Введите номер столика от 1 до ${restaurant.tableCount}`,
        max: restaurant.tableCount,
      }));
      return;
    }
    if (scheduled && !timeValid) {
      setError(t('orderType.timeInvalid', {
        defaultValue: todayHours
          ? `Время должно быть между ${todayHours.open} и ${todayHours.close}`
          : 'Введите корректное время в формате ЧЧ:ММ',
        open: todayHours?.open ?? '',
        close: todayHours?.close ?? '',
      }));
      return;
    }
    if (!isAuthenticated && showGuestPhone && !phoneValid) {
      setError(t('orderType.phoneRequired'));
      return;
    }

    setSubmitting(true);
    setError(null);

    let scheduledAt: string | undefined;
    if (scheduled && scheduledTime) {
      const today = new Date();
      const [h, m] = scheduledTime.split(':').map(Number);
      today.setHours(h, m, 0, 0);
      // If user picked time earlier than now today, push to tomorrow
      if (today.getTime() <= Date.now()) {
        today.setDate(today.getDate() + 1);
      }
      scheduledAt = today.toISOString();
    }

    let customerToken: string | null = null;
    let source: string | null = null;
    try {
      customerToken = localStorage.getItem('olso_customer_token');
      source = sessionStorage.getItem('olso_src');
    } catch {
      /* storage may be unavailable */
    }

    try {
      const res = await axios.post<{ qrCode: string; tableNumber: number | null }>(
        `${API_URL}/api/orders/customer`,
        {
          restaurantId: restaurant.id,
          items,
          phone: !isAuthenticated ? phone.trim() : undefined,
          source: source || undefined,
          fcmToken: fcmToken || undefined,
          orderType: atTable ? 'DINE_IN' : 'PICKUP',
          tableNumber: atTable ? tableNumberInt : undefined,
          scheduledAt: scheduledAt || undefined,
        },
        customerToken
          ? { headers: { Authorization: `Bearer ${customerToken}` } }
          : undefined
      );

      const { qrCode } = res.data;
      navigate(`/order/${qrCode}`);
    } catch (err: any) {
      setError(err?.response?.data?.error || t('common.unexpectedError'));
      setSubmitting(false);
    }
  };

  const handlePrimaryCta = () => {
    if (isAuthenticated || showGuestPhone) {
      submit();
      return;
    }
    onSignInRequired?.();
  };

  const submitDisabled =
    submitting ||
    items.length === 0 ||
    (atTable && !tableValid) ||
    (scheduled && !timeValid) ||
    (showGuestPhone && !phoneValid);

  return (
    <Sheet open={open} onClose={onClose}>
      <SheetHeader>
        <SheetTitle>{t('orderType.checkoutTitle', { defaultValue: 'Оформление заказа' })}</SheetTitle>
      </SheetHeader>
      <SheetBody>
        {/* Order summary */}
        {items.length > 0 && (
          <div className="ots-summary">
            <ul className="ots-summary-list">
              {items.map((it) => (
                <li key={it.productId} className="ots-summary-item">
                  <span className="ots-summary-qty">{it.quantity}×</span>
                  <span className="ots-summary-name">{it.name || it.productId}</span>
                  <span className="ots-summary-price">
                    {(it.price * it.quantity).toLocaleString('ru')} ₸
                  </span>
                </li>
              ))}
            </ul>
            <div className="ots-summary-total">
              <span>{t('orderType.total', { defaultValue: 'Итого' })}</span>
              <strong>{totalLabel} ₸</strong>
            </div>
          </div>
        )}

        {/* Where: pickup OR table number */}
        <div className="ots-section">
          <p className="ots-section-label">
            {t('orderType.howToReceive', { defaultValue: 'Куда принести?' })}
          </p>
          {canDineIn ? (
            <>
              <label className="ots-checkbox">
                <input
                  type="checkbox"
                  checked={atTable}
                  onChange={(e) => setAtTable(e.target.checked)}
                />
                <span>{t('orderType.atTable', { defaultValue: 'Я за столиком' })}</span>
              </label>
              {atTable && (
                <div className="ots-input-row">
                  <label className="ots-input-label" htmlFor="ots-table-input">
                    {t('orderType.tableNumberLabel', { defaultValue: 'Номер столика' })}
                  </label>
                  <input
                    id="ots-table-input"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={restaurant.tableCount}
                    placeholder={`1–${restaurant.tableCount}`}
                    className="ots-input ots-input--number"
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                  />
                </div>
              )}
              {!atTable && (
                <p className="ots-hint">
                  {t('orderType.pickupHint', { defaultValue: 'Подойдёте на стойку забрать заказ' })}
                </p>
              )}
            </>
          ) : (
            <p className="ots-hint">
              {t('orderType.pickupOnly', { defaultValue: 'На вынос — заказ соберут к выдаче' })}
            </p>
          )}
        </div>

        {/* When: now or scheduled (free time input) */}
        {canPreorder && (
          <div className="ots-section">
            <p className="ots-section-label">
              {t('orderType.whenToReceive', { defaultValue: 'Когда?' })}
            </p>
            <label className="ots-checkbox">
              <input
                type="checkbox"
                checked={scheduled}
                onChange={(e) => setScheduled(e.target.checked)}
              />
              <span>{t('orderType.scheduleToggle', { defaultValue: 'Хочу к определённому времени' })}</span>
            </label>
            {scheduled && (
              <div className="ots-input-row">
                <label className="ots-input-label" htmlFor="ots-time-input">
                  {t('orderType.timeLabel', { defaultValue: 'Время' })}
                </label>
                <input
                  id="ots-time-input"
                  type="time"
                  className="ots-input ots-input--time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={todayHours?.open}
                  max={todayHours?.close}
                />
                {todayHours && (
                  <p className="ots-hint">
                    {t('orderType.openWindow', {
                      defaultValue: `Сегодня открыто с ${todayHours.open} до ${todayHours.close}`,
                      open: todayHours.open,
                      close: todayHours.close,
                    })}
                  </p>
                )}
              </div>
            )}
            {!scheduled && (
              <p className="ots-hint">
                {t('orderType.asapHint', { defaultValue: 'Готовим сразу после оплаты' })}
              </p>
            )}
          </div>
        )}

        {/* Guest phone — revealed after "skip login" tap */}
        {!isAuthenticated && showGuestPhone && (
          <div className="ots-phone">
            <label className="ots-phone-label" htmlFor="ots-phone-input">
              {t('orderType.phoneLabel', { defaultValue: 'Телефон для связи' })}
            </label>
            <input
              id="ots-phone-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+7 700 123 45 67"
              className="ots-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        )}

        {error && <p className="ots-error" role="alert">{error}</p>}
      </SheetBody>

      <SheetActions>
        <Button
          variant="primary"
          size="lg"
          onClick={handlePrimaryCta}
          disabled={submitDisabled}
          aria-busy={submitting}
          className="ots-cta-primary"
        >
          {submitting
            ? t('common.loading')
            : isAuthenticated
              ? t('orderType.payCta', { defaultValue: 'Оплатить' }) + (total > 0 ? ` · ${totalLabel} ₸` : '')
              : showGuestPhone
                ? t('orderType.payCta', { defaultValue: 'Оплатить' }) + (total > 0 ? ` · ${totalLabel} ₸` : '')
                : t('orderType.signInAndPayCta', { defaultValue: 'Войти и оплатить' }) + (total > 0 ? ` · ${totalLabel} ₸` : '')}
        </Button>

        {!isAuthenticated && !showGuestPhone && (
          <button
            type="button"
            className="ots-skip-link"
            onClick={() => setShowGuestPhone(true)}
          >
            {t('orderType.skipLoginCta', {
              defaultValue: 'Не хочу бонусы — оплатить без входа',
            })}
          </button>
        )}
      </SheetActions>
    </Sheet>
  );
}
